import { format } from "date-fns";
import React, { useState, useRef, useEffect } from "react";
import config from "../config";

const dummyPartial = {
  number: "ORD-0016",
  date: "2025-08-20T00:00:00.000Z",
  currency: "INR",
  shop: {
    name: "SHOPTESTING",
    logoUrl: "logos/shop.png",
    address: "",
    phone: "3333333333",
    email: "",
    website: "",
  },
  customer: {
    firstname: "Unknown Customer",
    lastname: "",
    phone: "",
    email: "",
    address: {
      address1: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
    },
  },
  items: [
    {
      name: "Service/Product",
      type: "service",
      qty: 1,
      unitPrice: 224,
      notes: "Payment received",
    },
  ],
  payments: {
    method: "cash",
    amount: 224,
    date: "2025-08-20T00:00:00.000Z",
    ref: "N/A",
    cashier: "System",
  },
  discount: 0,
  shipping: 0,
  taxPercent: 0,
  notes: "Payment successful.",
};

export default function Receipt({
  shouldDownloadPdf = false,
  receipt = null,
  onSavePdf = null,
  onSavePdfComplete = null,
}) {
  const [generating, setGenerating] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const receiptRef = useRef(null);

  // Use the actual receipt if provided, otherwise use dummy data
  const usedReceipt = receipt || dummyPartial;
  
  const shop = usedReceipt?.shop || {};
  const customer = usedReceipt?.customer || {};
  const items = usedReceipt?.items || [];
  const payments = usedReceipt?.payments || {};

  const currency = usedReceipt?.currency || shop.currency || "INR";
  const receiptDate = usedReceipt?.date
    ? new Date(usedReceipt.date)
    : new Date();
  const S3_BASE_URL = config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL;

  // Get shop initials for the fallback box
  const getShopInitials = () => {
    if (!shop.name || shop.name.trim() === "") return "FL";
    
    const words = shop.name.split(' ').filter(word => word.length > 0);
    if (words.length === 0) return "FL";
    
    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase();
    }
    return words.map(word => word[0]).join('').slice(0, 2).toUpperCase();
  };

  // Build customer name - FIXED LOGIC
  const customerName = (() => {
    // First try client_name if it exists and is not empty
    if (customer.client_name && customer.client_name.trim()) {
      return customer.client_name.trim();
    }
    
    // Then try name field if it exists
    if (customer.name && customer.name.trim()) {
      return customer.name.trim();
    }
    
    // Then try combining firstname and lastname
    const firstName = customer.firstname?.trim() || "";
    const lastName = customer.lastname?.trim() || "";
    const fullName = [firstName, lastName].filter(Boolean).join(" ");
    
    if (fullName) {
      return fullName;
    }
    
    // Finally fall back to default
    return "Unknown Customer";
  })();

  // Build customer address (only if not empty)
  const customerAddress = customer.address
    ? [
        customer.address.address1,
        customer.address.address2,
        customer.address.city,
        customer.address.state,
        customer.address.zip,
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  const formatCurrency = (val) => {
    if (typeof val !== "number") val = Number(val) || 0;
    try {
      return val.toLocaleString(undefined, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
      });
    } catch {
      return `${currency} ${val.toFixed(2)}`;
    }
  };

  const subtotal = items.reduce(
    (s, it) => s + Number(it.unitPrice || 0) * (Number(it.qty) || 1),
    0
  );
  const total = subtotal;
  const paid = payments.amount || 0;

  // Save as PDF
  const handleSavePdf = async () => {
    if (!receiptRef.current) return;
    setGenerating(true);

    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pdfWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // ✅ Fit into one page
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);

      const blob = pdf.output("blob");
      const filename = `${usedReceipt?.number || "receipt"}.pdf`;
      const file = new File([blob], filename, { type: "application/pdf" });

      if (typeof onSavePdf === "function") {
        await onSavePdf(file);
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }

      if (typeof onSavePdfComplete === "function") {
        onSavePdfComplete({ success: true, file });
      }
    } catch (err) {
      console.error(err);
      alert("Failed to generate PDF.");
      if (typeof onSavePdfComplete === "function") {
        onSavePdfComplete({ success: false, error: err });
      }
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (shouldDownloadPdf) handleSavePdf();
  }, [shouldDownloadPdf]);

  // Reset logo error state when receipt changes
  useEffect(() => {
    setLogoError(false);
    setImageLoaded(false);
  }, [receipt]);

  return (
    <div
      ref={receiptRef}
      className="receipt-container max-w-md mx-auto bg-white shadow-md rounded-lg overflow-hidden print:max-w-full print:shadow-none print:rounded-none"
    >
      <div className="p-6 text-sm flex flex-col">
        {/* Header */}
        <div className="text-center font-semibold">RECEIPT</div>
        {/* ✅ Payment Success Tick */}
        <div className="flex justify-center mt-4">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-100 border-2 border-green-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>
        <p className="text-center text-green-600 font-semibold mt-2">
          Payment Successful
        </p>
        <div className="flex gap-2 items-center justify-start">
          <div>
            {shop.logoUrl && !logoError ? (
              <img 
                src={`${S3_BASE_URL}/${shop.logoUrl}`} 
                alt="logo" 
                className="mx-auto w-16 h-16 object-contain"
                onError={(e) => {
                  console.error("❌ Failed to load logo image");
                  setLogoError(true);
                }}
                onLoad={() => {
                  setImageLoaded(true);
                }}
              />
            ) : (
              <div className="mx-auto w-16 h-16 flex items-center justify-center font-bold bg-blue-500 text-white rounded-md text-lg">
                {getShopInitials()}
              </div>
            )}
          </div>
          <div>
            <h2 className="text-md font-bold uppercase tracking-wide">{shop.name || "FindLocalPetsServices"}</h2>
            <p className="text-xs text-gray-600">Address: {shop.address}</p>
            <p className="text-xs text-gray-600">Phone: {shop.phone} {shop.email && ("Email: " + shop.email)}</p>
            {shop.website && <p className="text-xs text-gray-600">{shop.website}</p>}
          </div>
        </div>

        <hr className="border-dashed mb-3" />

        {/* Receipt Meta */}
        <div className="flex justify-between text-sm mb-3">
          <div>
            <span className="font-semibold">Receipt #</span>
            <div>{usedReceipt.number}</div>
          </div>
          <div className="text-right">
            <span className="font-semibold">Date</span>
            <div>{format(receiptDate, "dd-MM-yyyy")}</div>
          </div>
        </div>

        <hr className="border-dashed mb-3" />

        {/* Customer */}
        <div className="mb-3 text-sm">
          <span className="font-semibold block">Received From:</span>
          <div>{customerName}</div>
          {customer.phone && <div>{customer.phone}</div>}
          {customer.email && <div>{customer.email}</div>}
          {customerAddress && <div>{customerAddress}</div>}
        </div>

        <hr className="border-dashed mb-3" />

        {/* Payment Details */}
        <div className="mb-3 text-sm">
          <span className="font-semibold block mb-1">Payment Details</span>
          <div className="flex justify-between mb-1">
            <span>Payment Method</span>
            <span className="capitalize">{payments.method}</span>
          </div>
          <div className="flex justify-between font-bold mt-2">
            <span>Paid Amount</span>
            <span>{formatCurrency(paid)}</span>
          </div>
        </div>

        <hr className="border-dashed mb-3" />

        {/* Notes */}
        {usedReceipt.notes && (
          <div className="text-sm mb-3">
            <span className="font-semibold block">Notes</span>
            <div>{usedReceipt.notes}</div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-gray-600 mt-6 border-t border-dashed pt-3">
          <p className="font-medium">Thank you for your payment!</p>
          <p>Visit again at {shop.name}</p>
        </div>
      </div>

      <style>{`
        @media print {
          body * { visibility: visible; }
          .no-print { display: none !important; }
          button { display: none !important; }
          .receipt-container {
            page-break-inside: avoid;
            max-height: 100%;
          }
        }
      `}</style>
    </div>
  );
}